
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Edit,
  History,
  FileText,
  User as UserIcon,
  Clock,
  CheckCircle,
  ArrowRight,
  ArrowLeftRight,
  Truck,
  MinusCircle,
  Plus
} from "lucide-react";
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { InventoryMovementItem, InventoryMovementHistory, Book } from '@/api/entities';

const statusColors = {
  draft: 'bg-gray-100 text-gray-800 border-gray-200',
  executed: 'bg-green-100 text-green-800 border-green-200'
};
const statusIcons = { draft: Clock, executed: CheckCircle };
const movementTypes = {
  receipt: { icon: Plus, color: 'bg-green-100 text-green-800' },
  transfer: { icon: ArrowLeftRight, color: 'bg-blue-100 text-blue-800' },
  shipment: { icon: Truck, color: 'bg-orange-100 text-orange-800' },
  write_off: { icon: MinusCircle, color: 'bg-red-100 text-red-800' }
};

export default function ViewInventoryMovementModal({ 
    isOpen, 
    onClose, 
    onEdit, 
    movement, 
    books, 
    items: availableItems, 
    warehouses 
}) {
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [history, setHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (movement && isOpen) {
            loadDetails();
        }
    }, [movement, isOpen]);

    const loadDetails = async () => {
        if (!movement) return;
        setIsLoading(true);
        try {
            // Use movement.items directly instead of calling InventoryMovementItem.get
            const itemsData = movement.items || [];
            setItems(itemsData);
            
            // Load history
            const historyData = await InventoryMovementHistory.list({ movement_id: movement.id });
            setHistory(historyData || []);
        } catch (error) {
            console.error(t('inventory_movement_view.error_loading_details'), error);
            setItems(movement.items || []);
            setHistory([]);
        }
        setIsLoading(false);
    };

    if (!isOpen || !movement) return null;

    const getItemInfo = (itemId) => {
        const item = availableItems.find(i => i.id === itemId);
        if (!item) return { name: t('inventory_movement_view.unknown_item'), identifier: 'N/A' };
        
        let identifier = 'N/A';
        if (item.type === 'book_edition' && item.metadata?.isbn) {
            identifier = `ISBN: ${item.metadata.isbn}`;
        } else if (item.type === 'simple_product' && item.metadata?.gtin) {
            identifier = `GTIN: ${item.metadata.gtin}`;
        } else if (item.type === 'bundle') {
            identifier = 'Bundle';
        }
        
        return {
            name: item.name || t('inventory_movement_view.unknown_item'),
            identifier: identifier,
            type: item.type
        };
    };

    const getWarehouseName = (id) => warehouses.find(w => w.id === id)?.name || t('inventory_movement_view.unknown_warehouse');
    const typeInfo = movementTypes[movement.document_type] || movementTypes.receipt;
    const StatusIcon = statusIcons[movement.status] || Clock;
    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
                <DialogHeader className="flex flex-row items-center justify-between">
                    <div>
                        <DialogTitle className="text-xl font-bold text-slate-900">{t('inventory_movement_view.title', { number: movement.document_number || movement.id })}</DialogTitle>
                        <p className="text-sm font-normal text-slate-500 mt-1">
                            {t('inventory_movement_view.date_from', { date: movement.document_date 
                              ? format(new Date(movement.document_date), 'dd MMMM yyyy', { locale: ru })
                              : format(new Date(movement.created_date), 'dd MMMM yyyy', { locale: ru })
                            })}
                        </p>
                    </div>
                    {movement.status === 'draft' && (
                        <Button onClick={() => onEdit(movement)} className="bg-amber-600 hover:bg-amber-700">
                            <Edit className="w-4 h-4 mr-2" />
                            {t('inventory_movement_view.edit')}
                        </Button>
                    )}
                </DialogHeader>

                <Tabs defaultValue="details" className="w-full mt-4">
                    <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="details">{t('inventory_movement_view.tabs.details')}</TabsTrigger>
                        <TabsTrigger value="history">{t('inventory_movement_view.tabs.history')}</TabsTrigger>
                    </TabsList>

                    <TabsContent value="details" className="space-y-6 mt-6">
                        <Card className="elegant-card">
                            <CardContent className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <p className="text-sm font-medium text-slate-500">{t('inventory_movement_view.operation_type')}</p>
                                    <Badge className={`${typeInfo.color} border font-medium text-base`}>
                                      {t(`inventory_movement.operation_types.${movement.document_type}`)}
                                    </Badge>
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-slate-500">{t('inventory_movement_view.status')}</p>
                                    <Badge className={`${statusColors[movement.status] || statusColors.draft} border font-medium text-base`}>
                                        <StatusIcon className="w-4 h-4 mr-2" />
                                        {movement.status === 'draft' ? t('inventory_movement_view.draft') : 
                                         movement.status === 'executed' ? t('inventory_movement_view.executed') : 
                                         movement.status || t('inventory_movement_view.unknown')}
                                    </Badge>
                                </div>
                                {movement.basis && (
                                  <div>
                                      <p className="text-sm font-medium text-slate-500">{t('inventory_movement.basis')}</p>
                                      <p className="font-semibold text-slate-900">{movement.basis}</p>
                                  </div>
                                )}
                            </CardContent>
                            {(movement.from_warehouse_id || movement.to_warehouse_id || movement.warehouse_id) && (
                                <CardContent className="p-6 border-t flex items-center justify-around">
                                    {movement.from_warehouse_id && (
                                        <div className="text-center">
                                            <p className="text-sm font-medium text-slate-500">{t('inventory_movement.from_warehouse')}</p>
                                            <p className="text-lg font-bold text-slate-900">{getWarehouseName(movement.from_warehouse_id)}</p>
                                        </div>
                                    )}
                                    {movement.from_warehouse_id && movement.to_warehouse_id && (
                                        <ArrowRight className="w-8 h-8 text-slate-400" />
                                    )}
                                    {movement.to_warehouse_id && (
                                        <div className="text-center">
                                            <p className="text-sm font-medium text-slate-500">{t('inventory_movement_view.to')}</p>
                                            <p className="text-lg font-bold text-slate-900">{getWarehouseName(movement.to_warehouse_id)}</p>
                                        </div>
                                    )}
                                    {/* Show main warehouse for receipt/shipment/write_off operations */}
                                    {!movement.from_warehouse_id && !movement.to_warehouse_id && movement.warehouse_id && (
                                        <div className="text-center">
                                            <p className="text-sm font-medium text-slate-500">
                                                {movement.document_type === 'receipt' ? t('inventory_movement.to_warehouse') :
                                                 movement.document_type === 'shipment' || movement.document_type === 'write_off' ? t('inventory_movement.from_warehouse') :
                                                 t('inventory_movement.warehouse')}
                                            </p>
                                            <p className="text-lg font-bold text-slate-900">{getWarehouseName(movement.warehouse_id)}</p>
                                        </div>
                                    )}
                                </CardContent>
                            )}
                        </Card>

                        <Card className="elegant-card">
                            <CardHeader>
                                <CardTitle className="flex justify-between items-center">
                                    <span>{t('inventory_movement_view.positions', { count: items.length })}</span>
                                    <span className="text-sm font-normal text-slate-500">{t('inventory_movement_view.total_items', { quantity: totalQuantity })}</span>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>{t('inventory_movement_view.item')}</TableHead>
                                            <TableHead>{t('inventory_movement_view.identifier')}</TableHead>
                                            <TableHead className="text-right">{t('inventory_movement_view.quantity')}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {items.map((item, index) => {
                                            const itemInfo = getItemInfo(item.item_id);
                                            return (
                                                <TableRow key={`${item.item_id}-${index}`}>
                                                    <TableCell className="font-medium">
                                                        <div>{itemInfo.name}</div>
                                                        {item.bundle_item_name && (
                                                            <div className="text-xs text-slate-500">
                                                                {t('inventory_movement_view.from_bundle')}: {item.bundle_item_name}
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="font-mono">{itemInfo.identifier}</TableCell>
                                                    <TableCell className="text-right font-bold">{item.quantity}</TableCell>
                                                </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="history" className="mt-6">
                        <Card className="elegant-card">
                           <CardHeader>
                               <CardTitle className="text-lg flex items-center gap-2">
                                   <History className="w-5 h-5 text-slate-600" />
                                   {t('inventory_movement_view.tabs.history')}
                               </CardTitle>
                           </CardHeader>
                           <CardContent>
                               {isLoading ? (
                                   <div className="text-center py-8">{t('notifications.info.loading')}</div>
                               ) : history.length === 0 ? (
                                   <div className="text-center py-8 text-slate-500">{t('inventory_movement_view.tabs.history')}</div>
                               ) : (
                                   <div className="space-y-4 max-h-96 overflow-y-auto">
                                       {history.map(h => (
                                           <div key={h.id} className="flex gap-3">
                                               <div className="mt-1"><UserIcon className="w-4 h-4 text-slate-400"/></div>
                                               <div>
                                                   <p className="font-medium text-slate-800">{h.details}</p>
                                                   <p className="text-xs text-slate-500">{h.user_name} • {format(new Date(h.created_date), 'dd.MM.yyyy HH:mm')}</p>
                                               </div>
                                           </div>
                                       ))}
                                   </div>
                               )}
                           </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
}
